import { v4 as uuidv4 } from 'uuid';
import type { InventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import { readInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import type { InventorizePhotoLlmClient } from '@/features/inventorization/inventorize-photo-llm-client';
import { type AiExtraction, CAPTURE_SESSION_STATUSES } from '@/shared/capture/capture-session';
import { AI_EXTRACTION_JOB_TYPES } from '@/shared/ocr/ai-extraction';
import { INVENTORIZE_EXTRACTION_BACKEND_STATUSES } from '@/shared/ocr/inventorize-extraction-contract';
import { mapBackendStatusToLocalStatus } from '@/shared/ocr/inventorize-extraction-jobs';

type LlmParseRepositories = Pick<InventoryAppServices, 'captureSessions' | 'photoBlobs'>;

export async function submitInventorizeLlmExtraction(input: {
  llmClient: InventorizePhotoLlmClient;
  locationApiKey: string;
  photoIds: string[];
  repositories: LlmParseRepositories;
  sessionId: string;
}) {
  const createdAt = new Date().toISOString();
  const session = await input.repositories.captureSessions.get(input.sessionId);

  if (!session) {
    throw new Error(`Capture session ${input.sessionId} was not found.`);
  }

  const photos = input.photoIds
    .map(photoId => session.photos.find(photo => photo.id === photoId))
    .filter(photo => photo !== undefined);
  const uploads = await Promise.all(
    photos.map(async photo => {
      const blobRecord = await input.repositories.photoBlobs.get(photo.localBlobId);

      if (!blobRecord) {
        throw new Error(`Photo blob ${photo.localBlobId} was not found.`);
      }

      return {
        blob: blobRecord.blob,
        photo,
      };
    }),
  );
  const commodity = readInventorizationIdentity(session).commodity;

  if (!commodity) {
    throw new Error('Commodity is required for LLM extraction.');
  }

  const response = await input.llmClient.createJob({
    commodity,
    locationApiKey: input.locationApiKey,
    photos: uploads,
    sessionId: input.sessionId,
  });
  const updatedAt = new Date().toISOString();
  const job: AiExtraction = {
    createdAt,
    id: `llm-${uuidv4()}`,
    jobType: AI_EXTRACTION_JOB_TYPES.geminiExtraction,
    output: {
      backendStatus: response.status,
      jobId: response.jobId,
      mode: 'server_job',
      photoIds: input.photoIds,
    },
    sessionId: input.sessionId,
    status: mapBackendStatusToLocalStatus(response.status),
  };

  if (response.status === INVENTORIZE_EXTRACTION_BACKEND_STATUSES.completed) {
    job.completedAt = updatedAt;
    job.output.result = response.result;
  }

  if (response.status === INVENTORIZE_EXTRACTION_BACKEND_STATUSES.failed) {
    job.completedAt = updatedAt;
    job.errorCode = response.error?.code ?? 'LLM_EXTRACTION_FAILED';
    job.output.error = response.error;
  }

  return await input.repositories.captureSessions.update(input.sessionId, currentSession => ({
    ...currentSession,
    aiExtractions: [...currentSession.aiExtractions, job],
    status:
      currentSession.status === CAPTURE_SESSION_STATUSES.draft
        ? CAPTURE_SESSION_STATUSES.needsReview
        : currentSession.status,
    updatedAt,
  }));
}
